/**
 * Ranked-item filtering.
 *
 * Unique weapons and armour are priced on their bonuses and rarity rather than
 * a market average, so they get their own matching rules instead of the
 * profit-% test used for stackable items.
 */

import type { Offer, RankedWatch } from "../state/types.ts";
import { WEAPON_BONUS_NAMES } from "../ui/links.ts";

const RARITY_RANK: Record<string, number> = { yellow: 1, orange: 2, red: 3 };

/**
 * Does this candidate match the watch's filters?
 *
 * `bargain` mode is deliberately loose: anything carrying a bonus or a rarity
 * under the price ceiling counts, which is how you find underpriced gear
 * without knowing in advance what you are looking for.
 */
export function rankedMatches(offer: Offer, watch: RankedWatch): boolean {
  // Display-case entries are never purchasable.
  if (offer.source === "display") return false;

  if (watch.maxPrice !== null && watch.maxPrice > 0 && offer.price > watch.maxPrice) return false;

  const details = offer.details;
  if (!details) return false;

  if (watch.mode === "bargain") {
    return details.bonuses.length > 0 || details.rarity !== null;
  }

  if (watch.rarityFloor) {
    const floor = RARITY_RANK[watch.rarityFloor] ?? 0;
    const actual = details.rarity ? (RARITY_RANK[details.rarity] ?? 0) : 0;
    if (actual < floor) return false;
  }

  const minValue = watch.minBonusValue ?? 0;

  if (watch.requiredBonuses.length === 0) {
    // No named bonus. The rarity floor above has already had its say, so with
    // no value threshold either there is nothing left to test — a watch with no
    // criteria at all is the user asking to see the whole cheap end of the list.
    if (minValue <= 0) return true;
    return details.bonuses.some((bonus) => bonus.value >= minValue);
  }

  const required = new Set(watch.requiredBonuses.map((name) => name.toLowerCase()));
  return details.bonuses.some(
    (bonus) => required.has(bonus.name.toLowerCase()) && bonus.value >= minValue,
  );
}

/**
 * Which `bonus` value to send to Torn's item-market endpoint.
 *
 * This must never come back empty. Torn sorts listings by price ascending, so
 * an unfiltered request returns the *cheapest* hundred — which for any ranked
 * item is a wall of plain, bonus-less copies. Measured on Yasukuni Sword: no
 * param gives 100 listings, none with a bonus, $150k–$450k; `bonus=Any` gives
 * 100 listings, all with bonuses, $48m–$210m. Unfiltered is not merely worse,
 * it is useless, and it costs a call either way.
 *
 * Torn accepts exactly one value, so a watch naming several bonuses falls back
 * to `Any` and is narrowed client-side by `rankedMatches`.
 */
export function tornBonusParam(watch: RankedWatch): string {
  // Bargain mode means "anything special", which is what `Any` selects for.
  if (watch.mode === "bargain") return "Any";

  if (watch.requiredBonuses.length === 1) {
    const wanted = (watch.requiredBonuses[0] as string).toLowerCase();
    const match = WEAPON_BONUS_NAMES.find((name) => name.toLowerCase() === wanted);
    if (match) return match;
  }

  // A rarity floor is expressible as a bonus filter and narrows harder than Any.
  if (watch.rarityFloor) return capitalise(watch.rarityFloor);

  return "Any";
}

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Merges the weav3r and Torn candidate lists.
 *
 * The same physical item can appear in both — weav3r indexes the item market
 * too — so `uid` is the dedupe key. Torn's copy wins on conflict: it is live,
 * where weav3r can be a minute stale.
 */
export function mergeCandidates(tornOffers: Offer[], weav3rOffers: Offer[]): Offer[] {
  const byUid = new Map<number, Offer>();

  for (const offer of weav3rOffers) {
    const uid = offer.details?.uid;
    if (uid === undefined) continue;
    byUid.set(uid, offer);
  }

  for (const offer of tornOffers) {
    const uid = offer.details?.uid;
    if (uid === undefined) continue;
    const existing = byUid.get(uid);
    // Keep the seller attribution weav3r has and Torn's anonymous market lacks.
    byUid.set(uid, existing ? { ...offer, sellerName: existing.sellerName ?? null } : offer);
  }

  return [...byUid.values()].sort((a, b) => a.price - b.price);
}

/** Stable identity for a ranked candidate, for alert de-duplication. */
export function rankedOfferKey(offer: Offer): string {
  return `ranked:${offer.itemId}:${offer.details?.uid ?? "-"}:${offer.price}`;
}

export function rankedWatchId(): string {
  return `r_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}
